import { ActivityBoardItem, ActivityType, ShortBook } from 'library/types';

export type Tab3ActivityTypeRow = {
  activityTypeId: number;
  activityTypeTitle: string;
  spentHours: number;
  overtimeHours: number;
  totalHours: number;
};

export type Tab3BookSummaryRow = {
  bookId: number;
  bookTitle: string;
  spentHours: number;
  overtimeHours: number;
  totalHours: number;
  activityTypeRows: Tab3ActivityTypeRow[];
};

type Tab3GrandTotal = {
  spentHours: number;
  overtimeHours: number;
  totalHours: number;
};

type Tab3ChartData = {
  yLabels: string[];
  values: number[];
};

const getBookTitle = (book: ShortBook): string | null => {
  const title: unknown = book.title;

  if (typeof title !== 'string') return null;

  const trimmedTitle = title.trim();
  return trimmedTitle || null;
};

export const prepareTab3Books = (books: ShortBook[]): ShortBook[] => {
  const validBooks = books.reduce<ShortBook[]>((result, book) => {
    const title = getBookTitle(book);

    if (title) result.push({ ...book, title });

    return result;
  }, []);

  return validBooks.sort((a, b) => a.title.localeCompare(b.title));
};

export const buildTab3BookSummaryRows = (
  activityBoardItems: ActivityBoardItem[],
  books: ShortBook[],
  activityTypes: ActivityType[]
): Tab3BookSummaryRow[] => {
  const bookTitlesById = new Map(prepareTab3Books(books).map((book) => [book.id, book.title]));
  const bookMap = new Map<number, Map<number, { spentHours: number; overtimeHours: number }>>();

  for (const item of activityBoardItems) {
    if (!bookTitlesById.has(item.bookId)) continue;

    if (!bookMap.has(item.bookId)) {
      bookMap.set(item.bookId, new Map());
    }

    const activityTypeMap = bookMap.get(item.bookId)!;
    if (!activityTypeMap.has(item.activityTypeId)) {
      activityTypeMap.set(item.activityTypeId, { spentHours: 0, overtimeHours: 0 });
    }

    const entry = activityTypeMap.get(item.activityTypeId)!;
    if (item.isOvertimeWork) {
      entry.overtimeHours += item.estimatedHours;
    } else {
      entry.spentHours += item.estimatedHours;
    }
  }

  const rows: Tab3BookSummaryRow[] = [];

  bookMap.forEach((activityTypeMap, bookId) => {
    const bookTitle = bookTitlesById.get(bookId);

    if (!bookTitle) return;

    const activityTypeRows: Tab3ActivityTypeRow[] = [];
    let totalSpent = 0;
    let totalOvertime = 0;

    activityTypeMap.forEach((value, activityTypeId) => {
      const activityType = activityTypes.find((item) => item.id === activityTypeId);
      const spentHours = parseFloat(value.spentHours.toFixed(2));
      const overtimeHours = parseFloat(value.overtimeHours.toFixed(2));

      activityTypeRows.push({
        activityTypeId,
        activityTypeTitle: activityType?.title || `Activity Type #${activityTypeId}`,
        spentHours,
        overtimeHours,
        totalHours: parseFloat((value.spentHours + value.overtimeHours).toFixed(2)),
      });
      totalSpent += value.spentHours;
      totalOvertime += value.overtimeHours;
    });

    activityTypeRows.sort((a, b) => b.totalHours - a.totalHours);

    rows.push({
      bookId,
      bookTitle,
      spentHours: parseFloat(totalSpent.toFixed(2)),
      overtimeHours: parseFloat(totalOvertime.toFixed(2)),
      totalHours: parseFloat((totalSpent + totalOvertime).toFixed(2)),
      activityTypeRows,
    });
  });

  return rows.sort((a, b) => a.bookTitle.localeCompare(b.bookTitle));
};

export const calculateTab3GrandTotal = (rows: Tab3BookSummaryRow[]): Tab3GrandTotal => {
  const spentHours = parseFloat(rows.reduce((sum, row) => sum + row.spentHours, 0).toFixed(2));
  const overtimeHours = parseFloat(rows.reduce((sum, row) => sum + row.overtimeHours, 0).toFixed(2));

  return {
    spentHours,
    overtimeHours,
    totalHours: parseFloat((spentHours + overtimeHours).toFixed(2)),
  };
};

export const buildTab3ChartData = (rows: Tab3BookSummaryRow[]): Tab3ChartData => {
  const sortedRows = rows.filter((row) => row.totalHours > 0).sort((a, b) => b.totalHours - a.totalHours);

  return {
    yLabels: sortedRows.map((row) => row.bookTitle),
    values: sortedRows.map((row) => row.totalHours),
  };
};
